import { select } from '@angular-redux/store';
import { ChangeDetectorRef, Component, forwardRef, Inject, ViewChild } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Company } from 'sharedFeatures/sharedModels';
import { BillingService, DialogCreationService, ProfileService } from "sharedFeatures/sharedServices";
import { ICreateCompanyResponse, IErrorResponse, isSuccessResponse } from "sharedFeatures/sharedServices/Responses/billing.index";
import { Utils } from 'sharedFeatures/utils';
import { ButtonComponent } from 'sharedGuiSmall/components/button/button.component';
import { SharedGuiSmallModule } from '../../../../sharedGuiSmall/sharedGuiSmall.module';
import { ScrollComponent } from '../scroll/scrolldiv.component';
import { NewCompanyWin } from './new-company-win.component';
import { LoadingSpinnerService } from 'sharedGuiSmall/sharedServices/loadingSpinner.service';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'new-company-win-body',
    templateUrl: './new-company-win-body.component.html',
    styleUrls: ['./new-company-win-body.component.less'],
    standalone: true,
    imports: [SharedGuiSmallModule, ScrollComponent, TranslateModule]
})
export class NewCompanyWinBodyComponent {

    @ViewChild(ButtonComponent)
    button!: ButtonComponent;

    Company: Company = new Company();
    OldCompany: Company = new Company();

    title: string = '';
    companyBtnText = '';

    checkClass: string = 'checked';
    private c3Color = '#D96C6C';
    private g4Color = '#a5b1be';
    private b1Color = '#404a5f';
    //isSelectedCompanyTeamOwner: boolean = false;

    loadFinished: boolean = false;

    initialCountryId: number = 0;
    initialIndustryId: number = 0;

    parentComp: NewCompanyWin | undefined = undefined;

    height: number = 325;
    width = 700;

    // Unsubscribe event
    private storeUnsubscribe = new Subject<void>();

    /*************** main_app store **********************/
    @select(['appData', 'appComponent'])
    readonly appComponent$!: Observable<any>;
    private appComponent: any = null;
    /*****************************************************/

    /*************** account_app store *******************/
    @select(['accountAppData', 'accountAppComponent'])
    readonly accountAppComponent$!: Observable<any>;
    private accountAppComponent: any = null;
    /*****************************************************/


    @select(['account', 'companyChanged'])
    readonly companyChanged$!: Observable<boolean>;

    titleText = $localize`CREATE_AN_ORGANISATION_FOR`;
    createOrgAndStartTrialText = $localize`CREATE_ORGANISATION_AND_START`;
    trialSubAssignedText = $localize`Trial subscription assigned`;
    createCompanyErrorText = $localize`Create trial company error`;
    constructor(
        @Inject(forwardRef(() => NewCompanyWin))
        public parent: NewCompanyWin,
        private billingService: BillingService,
        private profileService: ProfileService,
        private dialogCreation: DialogCreationService,
        private chDetRef: ChangeDetectorRef,
        private loadingSpinnerService: LoadingSpinnerService) {
        this.parentComp = parent;
        let companyId = localStorage.getItem('selectedCompanyId') ?? '0';
        this.profileService.LoadCompany(companyId);
    }

    ngOnInit() {
        // Subscribe on the redux store
        this.subscribeReduxStore();

        this.loadingSpinnerService.setLoading(false);

        this.title = this.titleText;
        this.companyBtnText = this.createOrgAndStartTrialText;
    }

    ngAfterViewInit() {
        this.loadForm();
    }

    // Subscribe on the redux store
    private subscribeReduxStore(): void {

        this.appComponent$.pipe(
            takeUntil(this.storeUnsubscribe))
            .subscribe((x: any) => this.appComponent = x);

        this.accountAppComponent$.pipe(
            takeUntil(this.storeUnsubscribe))
            .subscribe(x => this.accountAppComponent = x);
    }

    public loadForm(): void {
        if (this.profileService.companyPromise != undefined)
            this.profileService.companyPromise.then((data: string) => {

                if (data && !Utils.isNullOrEmpty(data)) {
                    let result = <Company>JSON.parse(data).data;
                    this.Company = new Company(result);
                    this.Company.CompanyName = `${this.Company.CompanyName} Trial`;

                }
            });
    }

    onClose() {
        this.parent.close();
    }

    refreshAppArea(): void {
        window.location.href = window.appPath + 'Account';
    }

    refreshAccountAppArea(): void {
        this.accountAppComponent.reloadAuthData(true, () => {
            this.accountAppComponent.menu.reloadAuthData();
            this.accountAppComponent.myplans.loadCompanies();

            const popup = this.dialogCreation.createErrorWarning(
                this.trialSubAssignedText,
                $localize`Trial subscription assigned successfully to organisation ${this.Company.CompanyName}`, () => {
                    popup.close();
                    this.loadingSpinnerService.setLoading(false);
                }, 250, 'OK', 400);

            popup.show();

        });
    }

    saveClick(): void {
        if (!this.HasChanges()) return;

        this.parent.close();
        this.loadingSpinnerService.setLoading(true);

        this.OldCompany = JSON.parse(JSON.stringify(this.Company));

        this.billingService.CreateCompany(this.Company.CompanyName, this.Company.Industry_ID, this.Company.Country_ID, this.Company.WebSite, true)
            .then((data: ICreateCompanyResponse | IErrorResponse) => {
                if (isSuccessResponse(data)) {
                    data = data as ICreateCompanyResponse;
                    if (!Utils.isNumeric(data.companyId))
                        throw new Error(`company-win-body.saveClick: selectedCompanyId:${data.companyId} is not numeric`);

                    localStorage.setItem('selectedCompanyId', data.companyId + '');

                    if (this.accountAppComponent) // BR-8093 TODO refactoring
                        this.refreshAccountAppArea();
                    else
                        this.refreshAppArea();
                }
                else {
                    const popup = this.dialogCreation.createErrorWarning(this.createCompanyErrorText, (<IErrorResponse>data).errorMessage, () => { popup.close(); });
                    popup.show();
                    this.loadingSpinnerService.setLoading(false);
                }

            })
            .catch(error => {
                this.dialogCreation.createUnknownExceptionDialog(error).show();
                this.loadingSpinnerService.setLoading(false);
            });
    }

    HasChanges(): boolean {
        return !this.Company.IsEqual(this.OldCompany);
    }

    ngAfterViewChecked(): void {
        if (!this.HasChanges() || Utils.isNullOrEmpty(this.Company.CompanyName)) this.button.disable();
        else this.button.enable();
    }

    ngOnDestroy(): void {
        // Unsubscribe from the redux store
        this.storeUnsubscribe.next();
        this.storeUnsubscribe.complete();
    }
}
